import { Suspense, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Keyboard, Menu, Search, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useFilial } from '@/contexts/FilialContext';
import { useRecursosPlano } from '@/hooks/useRecursosPlano';
import { navSections } from '@/config/navigation';
import { filialLabel } from '@/lib/filial-label';
import { Skeleton } from '@/components/ui/skeleton';
import { PageSkeleton } from '@/components/layout/PageSkeleton';
import { NotificacoesBell } from '@/components/notificacoes/NotificacoesBell';

/** Tique colorido por grupo de menu — DNA visual do rail (docs 13 §6.2). */
const sectionTicks: Record<string, string> = {
  Vendas: 'bg-brand',
  Cadastros: 'bg-azure',
  Estoque: 'bg-success',
  Gestão: 'bg-primary',
  Administração: 'bg-slate-400',
};

function NavGroup({
  title,
  items,
  onNavigate,
  pdvChrome,
}: {
  title: string;
  items: (typeof navSections)[0]['items'];
  onNavigate?: () => void;
  pdvChrome?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div className="px-2 py-1.5">
      <p className="mb-1 flex items-center gap-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--rail-fg-muted))]/70">
        <span
          className={cn(
            'h-[3px] w-2 rounded-full',
            // No azure do modo caixa os tiques coloridos somem — viram brancos
            pdvChrome ? 'bg-white/70' : (sectionTicks[title] ?? 'bg-slate-400'),
          )}
          aria-hidden
        />
        {title}
      </p>
      <div className="flex flex-col gap-0.5">
        {items.map(({ to, label, icon: Icon, end: endLink, recursoExigido }) => (
          <NavLink
            key={to}
            to={to}
            end={endLink ?? to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                // Quick 260820-dqz: item gateado por plano entra suavemente
                // quando o recurso liga (ex.: superadmin troca o plano na
                // sessão anterior sem exigir novo login) — nunca "pop".
                recursoExigido && 'animate-in fade-in duration-200',
                isActive
                  ? pdvChrome
                    ? 'bg-gradient-to-r from-brand to-[hsl(22_85%_46%)] text-white font-semibold shadow-[0_4px_14px_hsl(var(--brand)/0.4)]'
                    : 'bg-gradient-to-r from-azure to-primary text-white font-semibold shadow-[0_4px_14px_hsl(var(--azure)/0.45)]'
                  : 'text-[hsl(var(--rail-fg-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--rail-fg))]',
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AppShell() {
  const { user, logout, isImpersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { filialId, setFilialId, filiais, isLoading, isError, refetchFiliais } = useFilial();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const userPerfis = useMemo(() => user?.perfis ?? [], [user]);
  // Quick 260820-dqz: recursos do plano da organização — item com
  // `recursoExigido` só aparece quando o recurso estiver ligado. Enquanto
  // carrega, o hook devolve os três recursos desligados (fail-closed), então
  // o item permanece oculto até a leitura do plano confirmar — nunca aparece
  // e depois some.
  const { recursos } = useRecursosPlano();
  const navSectionsVis = useMemo(
    () =>
      navSections
        .map((section) => ({
          ...section,
          items: section.items.filter((it) => {
            if (it.anyPerfil?.length && !it.anyPerfil.some((p) => userPerfis.includes(p))) return false;
            if (it.recursoExigido && !recursos[it.recursoExigido]) return false;
            return true;
          }),
        }))
        .filter((s) => s.items.length > 0),
    [userPerfis, recursos],
  );
  const brandHomePath = useMemo(() => {
    const painelItem = navSectionsVis.flatMap((section) => section.items).find((item) => item.to === '/');
    if (painelItem) return painelItem.to;
    return navSectionsVis[0]?.items[0]?.to ?? '/';
  }, [navSectionsVis]);

  const closeMobileNav = () => setMobileNavOpen(false);
  const isPdvRoute = pathname === '/pdv' || pathname.startsWith('/pdv/');

  const handleSair = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <a
        href="#conteudo-principal"
        className="fixed left-4 top-2 z-[100] -translate-y-[200%] rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-md transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Ir para o conteúdo
      </a>

      <aside
        className={cn(
          'hidden w-56 flex-col text-[hsl(var(--rail-fg))] md:flex',
          'bg-gradient-to-b shadow-[inset_-1px_0_0_0_hsl(213_45%_60%/0.16)]',
          isPdvRoute
            ? 'from-[hsl(var(--pdv-rail-from))] to-[hsl(var(--pdv-rail-to))]'
            : 'from-[hsl(var(--rail-from))] to-[hsl(var(--rail-to))]',
        )}
      >
        <NavLink
          to={brandHomePath}
          className="flex h-14 items-center gap-2.5 border-b border-white/10 px-4 transition-colors hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={brandHomePath === '/' ? 'Ir para o painel' : 'Ir para página inicial disponível'}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center">
            <img src="/logo-simbolo-branco.png" alt="" aria-hidden className="h-7 w-7 object-contain" />
          </span>
          <span className="min-w-0 leading-none">
            <span className="font-display text-[15px] font-bold text-white">
              Agiliza<span className="text-brand">OS</span>
            </span>
            <span className="mt-0.5 block text-[9px] tracking-wide text-[hsl(var(--rail-fg-muted))]/80">
              Gestão para oficinas
            </span>
          </span>
        </NavLink>

        <nav className="flex-1 overflow-y-auto py-1.5" aria-label="Principal">
          {navSectionsVis.map((section) => (
            <NavGroup
              key={section.title}
              title={section.title}
              items={section.items}
              pdvChrome={isPdvRoute}
            />
          ))}
        </nav>

        {/* Linhas de velocidade da logo — assinatura visual do rail */}
        <div className="space-y-1.5 px-5 pb-3 pt-2" aria-hidden>
          <span className={cn('block h-[3px] w-2/5 rounded-full', isPdvRoute ? 'bg-white/40' : 'bg-azure/35')} />
          <span className={cn('block h-[3px] w-3/5 rounded-full', isPdvRoute ? 'bg-white/25' : 'bg-azure/20')} />
          <span className={cn('block h-[3px] w-1/4 rounded-full', isPdvRoute ? 'bg-white/15' : 'bg-azure/10')} />
        </div>

        <div className="flex items-center gap-2.5 border-t border-white/10 p-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-azure to-primary text-xs font-bold text-white"
            aria-hidden
          >
            {user?.nome?.charAt(0).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-xs font-semibold text-[hsl(var(--rail-fg))]">
              {user?.nome}
            </span>
            <span className="block truncate text-[10px] text-[hsl(var(--rail-fg-muted))]/80">
              {(() => {
                const f = filiais.find((x) => x.id === filialId);
                return f ? filialLabel(f) : (user?.organizacao?.nome ?? '');
              })()}
            </span>
          </span>
          <span className="flex flex-col items-end">
            <button
              type="button"
              onClick={handleSair}
              className="text-[10.5px] text-[hsl(var(--rail-fg-muted))] underline underline-offset-2 transition-colors hover:text-white"
            >
              Sair
            </button>
          </span>
        </div>
      </aside>

      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent
          className={cn(
            'fixed left-0 top-0 z-50 flex h-full max-h-screen w-[min(100vw-1rem,20rem)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 shadow-xl sm:max-w-none',
            'data-[state=closed]:slide-out-to-left-2 data-[state=open]:slide-in-from-left-2',
            'bg-gradient-to-b text-[hsl(var(--rail-fg))]',
            isPdvRoute
              ? 'from-[hsl(var(--pdv-rail-from))] to-[hsl(var(--pdv-rail-to))]'
              : 'from-[hsl(var(--rail-from))] to-[hsl(var(--rail-to))]',
          )}
        >
          <DialogHeader className="border-b border-white/10 px-4 py-3 text-left">
            <DialogTitle className="text-[hsl(var(--rail-fg))]">
              <NavLink
                to={brandHomePath}
                onClick={closeMobileNav}
                className="inline-flex items-center gap-2.5 rounded-sm text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={brandHomePath === '/' ? 'Ir para o painel' : 'Ir para página inicial disponível'}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                  <img src="/logo-simbolo-branco.png" alt="" aria-hidden className="h-7 w-7 object-contain" />
                </span>
                <span className="font-display text-[15px] font-bold text-white">
                  Agiliza<span className="text-brand">OS</span>
                </span>
              </NavLink>
            </DialogTitle>
            <DialogDescription className="sr-only">Menu principal</DialogDescription>
          </DialogHeader>
          <nav className="flex-1 overflow-y-auto py-1.5" aria-label="Principal">
            {navSectionsVis.map((section) => (
              <NavGroup
                key={section.title}
                title={section.title}
                items={section.items}
                onNavigate={closeMobileNav}
                pdvChrome={isPdvRoute}
              />
            ))}
          </nav>
          <div className="border-t border-white/10 p-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start text-[hsl(var(--rail-fg-muted))] hover:bg-white/[0.06] hover:text-white"
              onClick={() => {
                closeMobileNav();
                handleSair();
              }}
            >
              Sair
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className={cn(
            'flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-3 shadow-[0_1px_0_0_hsl(var(--border))] sm:px-4',
            isPdvRoute && 'border-[hsl(var(--pdv-border))] bg-[hsl(var(--pdv-bg))] text-[hsl(var(--pdv-fg))] shadow-none',
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 md:hidden"
              aria-label="Abrir menu de navegação"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </Button>
            <div className="hidden h-5 w-px bg-border md:block" aria-hidden />
            {isPdvRoute && (
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gradient-to-r from-brand to-[hsl(22_85%_46%)] px-2.5 py-1 font-os text-[10px] font-bold tracking-[0.1em] text-white shadow-[0_2px_8px_hsl(var(--brand)/0.45)]"
                aria-label="Você está no modo caixa"
              >
                <Store className="h-3 w-3" aria-hidden />
                MODO CAIXA
              </span>
            )}
            <span className={cn('hidden text-sm sm:inline', isPdvRoute ? 'text-[hsl(var(--pdv-fg-muted))]' : 'text-muted-foreground')}>Filial</span>
            {isLoading ? (
              <Skeleton className="h-11 w-[140px] sm:h-10 sm:w-[200px]" aria-hidden />
            ) : isError ? (
              <span className="flex items-center gap-2 text-sm text-destructive">
                Erro
                <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => refetchFiliais()}>
                  Tentar
                </Button>
              </span>
            ) : filiais.length === 0 ? (
              <span className="text-sm text-muted-foreground">Sem filiais</span>
            ) : (
              <Select value={filialId} onValueChange={setFilialId}>
                <SelectTrigger
                  className={cn(
                    'h-11 sm:h-10 w-[140px] sm:w-[200px]',
                    isPdvRoute && 'border-[hsl(var(--pdv-border))] bg-[hsl(var(--pdv-surface))] text-[hsl(var(--pdv-fg))]',
                  )}
                  style={
                    isPdvRoute
                      ? {
                          backgroundColor: 'hsl(var(--pdv-surface))',
                          borderColor: 'hsl(var(--pdv-border))',
                          color: 'hsl(var(--pdv-fg))',
                        }
                      : undefined
                  }
                >
                  <SelectValue placeholder="Selecione a filial" />
                </SelectTrigger>
                <SelectContent>
                  {filiais.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {filialLabel(f)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <span className={cn('hidden text-xs lg:inline', isPdvRoute ? 'text-[hsl(var(--pdv-fg-muted))]' : 'text-muted-foreground')}>
              <Keyboard className="mr-1 inline h-3.5 w-3.5" aria-hidden />
              F2 busca placa
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isPdvRoute && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 border-[hsl(var(--pdv-border))] bg-[hsl(var(--pdv-surface))] text-[hsl(var(--pdv-fg))] hover:bg-[hsl(var(--pdv-surface-2))] hover:text-[hsl(var(--pdv-fg))]"
                onClick={() => navigate(`/pdv?buscarOs=${Date.now()}`)}
                aria-label="Buscar OS (F4)"
              >
                <Search className="h-4 w-4 sm:mr-1.5" aria-hidden />
                <span className="hidden sm:inline">Buscar OS</span>
                <span className="hidden lg:inline"> (F4)</span>
              </Button>
            )}
            {/* Slot para ações rápidas do PDV (troca, devolução, pendentes, finalizadas) —
                preenchido via portal pela PdvPage quando o caixa está aberto. Fica vazio
                (sem tamanho) em qualquer outra rota. */}
            {isPdvRoute && <div id="pdv-header-actions" className="flex shrink-0 items-center gap-1.5" />}
            <NotificacoesBell />
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                isPdvRoute
                  ? 'bg-[hsl(var(--pdv-surface-2))] text-[hsl(var(--pdv-fg))]'
                  : 'bg-gradient-to-br from-azure to-primary text-white',
              )}
              aria-hidden
            >
              {user?.nome?.charAt(0).toUpperCase()}
            </span>
            <span
              className={cn(
                'hidden max-w-[120px] truncate text-sm sm:inline',
                isPdvRoute ? 'text-[hsl(var(--pdv-fg))]' : 'text-foreground',
              )}
            >
              {user?.nome}
            </span>
          </div>
        </header>

        {isImpersonating && (
          <div className="flex items-center justify-between gap-3 bg-brand px-4 py-2 text-sm font-medium text-brand-foreground">
            <span>
              Representando <strong>{user?.nome}</strong> ({user?.organizacao?.nome}) — token expira em 2h
            </span>
            <button
              type="button"
              onClick={() => { stopImpersonating(); navigate('/superadmin', { replace: true }); }}
              className="rounded-md border border-white/40 bg-white/10 px-3 py-0.5 text-xs font-semibold hover:bg-white/20"
            >
              Voltar ao painel admin
            </button>
          </div>
        )}

        <main
          id="conteudo-principal"
          className="flex-1 overflow-auto p-4 md:p-6"
          tabIndex={-1}
          style={{
            /* Luz de topo — lavagem sutil azure/laranja atrás do cabeçalho de
               cada página (docs 13 §5.1); mata o fundo chapado de admin. */
            backgroundImage:
              'radial-gradient(90% 160px at 18% 0%, hsl(var(--azure) / 0.12), transparent 70%), radial-gradient(55% 130px at 88% 0%, hsl(var(--brand) / 0.06), transparent 70%)',
          }}
        >
          {/* Shell (sidebar/header) fica montado; só o conteúdo da rota suspende
              enquanto o chunk lazy carrega — ver App.tsx e regra de lazy loading
              em docs-arquitetura/13-diretrives-visuais-ux.md.md §10.2. */}
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
